import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { PrivacyUrl, TermsUrl } from '@/environment';
import checkedBox from '@/images/Checkbox.svg';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import uploadAudio from '@/pages/editor/components/useUploadCloneVoice';
import { useAuth } from '@/services/auth';
import { SupportedLanguage } from '@/utils/types';
import styled from '@emotion/styled';
import { Collapse, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { CloneVoiceLanguageSelect } from './CloneVoiceLanguageSelect';
import { NameYourVoice, PromptContainer, PromptText, SubTitle, TitleWithBackButton } from './common';
import { consentContentByLanguage, rtlLanguages } from './textByLanguage';
import { VoiceUploadBox } from './VoiceUploadBox';

const CheckBokInfo = styled(Typography)`
    font-size: 14px;
    line-height: 20px;
    width: 90%;
    color: ${({ theme }) => theme.colors.black[900]};
`;

const LinkText = styled.label`
    color: ${({ theme }) => theme.colors.links.main};
    cursor: pointer;
`;

const Content = styled.div`
    display: flex;
    gap: 10px;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 32px;
`;

const StyledSubTitle = styled(SubTitle)`
    margin-top: 16px;
    margin-bottom: 32px;
`;

const SubmitButton = styled(Button)`
    height: 48px;
    width: 100%;
`;

const EmptyBox = styled.div`
    height: 20px;
    width: 20px;
    border-radius: 4px;
    border: 1px solid var(--secondary-shades-500, #82807e);
`;

const AgreementText = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 32px;
    cursor: pointer;
`;

interface Props {
    onBackClick: () => void;
    onSubmit: (args: { file: string; name: string; language?: SupportedLanguage }) => void;
}

function UploadVoiceDialogContent({ onBackClick, onSubmit }: Props) {
    const [language, setLanguage] = useState<SupportedLanguage>('english');
    const { upload, reset: resetUploadedAudio } = uploadAudio();

    const [audioData, setAudioData] = useState<AudioData | null>(null);
    const [voiceName, setVoiceName] = useState('');
    const [agreementChecked, setAgreementChecked] = useState(false);
    const { user } = useAuth();
    const theme = useTheme();
    const [checked, setChecked] = useState(false);

    const defaultName = user?.given_name || 'Untitled voice';
    const skipConsent = user?.features?.includes('tts-voice-consent:skip');

    return !skipConsent ? (
        <>
            <TitleWithBackButton title="Upload file" onBackClick={onBackClick} />
            <StyledSubTitle variant="body1">Upload a sample of your voice that begins with the consent script below</StyledSubTitle>
            <CloneVoiceLanguageSelect
                value={language}
                onChange={nextLanguage => {
                    setLanguage(nextLanguage);
                }}
            />
            <PromptContainer>
                <PromptText align={rtlLanguages.includes(language) ? 'right' : 'left'} variant="h4">
                    {consentContentByLanguage[language] || consentContentByLanguage.english}
                </PromptText>
            </PromptContainer>
            <VoiceUploadBox
                onFileSelect={(file: File) => upload(file)}
                audioData={audioData}
                onAudioUploaded={data => {
                    setAudioData(data);
                    resetUploadedAudio();
                    setAgreementChecked(false);
                }}
                onAudioUploadError={() => {
                    setAudioData(null);
                }}
                onDelete={() => {
                    setAudioData(null);
                }}
            />
            <NameYourVoice
                value={voiceName}
                onChange={e => {
                    setVoiceName(e.target.value);
                }}
                placeholder={defaultName}
            />
            <Collapse in={!!audioData} unmountOnExit timeout="auto">
                <AgreementText data-testid="clone-voice-agreement-text" onClick={() => setAgreementChecked(!agreementChecked)}>
                    {agreementChecked ? <MaskedIcon src={checkedBox} size="20px" color={theme.colors.secondary.main} /> : <EmptyBox />}
                    <Typography variant="body1" fontWeight={500}>
                        The uploaded file includes the consent script above at the start of the recording.
                    </Typography>
                </AgreementText>
            </Collapse>
            <SubmitButton
                color="primary"
                data-testid="clone-voice-submit-button"
                disabled={!audioData || !agreementChecked}
                onClick={() =>
                    onSubmit({
                        file: audioData!.data as string,
                        name: voiceName || defaultName,
                        language,
                    })
                }>
                Clone voice
            </SubmitButton>
        </>
    ) : (
        <>
            <TitleWithBackButton title="Upload file" onBackClick={onBackClick} />
            <VoiceUploadBox
                onFileSelect={(file: File) => upload(file)}
                audioData={audioData}
                onAudioUploaded={data => {
                    setAudioData(data);
                    resetUploadedAudio();
                    setAgreementChecked(false);
                }}
                onAudioUploadError={() => {
                    setAudioData(null);
                }}
                onDelete={() => {
                    setAudioData(null);
                }}
            />
            <NameYourVoice
                value={voiceName}
                onChange={e => {
                    setVoiceName(e.target.value);
                }}
                placeholder={defaultName}
            />
            <Content>
                {checked ? (
                    <MaskedIcon src={checkedBox} size="20px" color={theme.colors.secondary.main} onClick={() => setChecked(!checked)} />
                ) : (
                    <EmptyBox onClick={() => setChecked(!checked)} />
                )}
                <CheckBokInfo>
                    By checking the box I state that I am the owner or that I have any and all of the necessary
                    rights/licenses/permissions/consents in the voice recording uploaded required for the use and cloning by the D-ID
                    Product and that any use of the output generated by the D-ID Product will be subject to D-ID’s
                    <LinkText onClick={() => window.open(TermsUrl, '_blank')}>Terms of Use</LinkText> and{' '}
                    <LinkText onClick={() => window.open(PrivacyUrl, '_blank')}>Privacy Policy</LinkText>.
                </CheckBokInfo>
            </Content>

            <SubmitButton
                color="primary"
                disabled={!audioData || !checked}
                onClick={() =>
                    onSubmit({
                        file: audioData!.data as string,
                        name: voiceName || defaultName,
                        language,
                    })
                }>
                Clone voice
            </SubmitButton>
        </>
    );
}

export default UploadVoiceDialogContent;
